import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { qrReviewApi } from './qr-review.api';
import { qrReviewKeys } from './qr-review.queries';
import type {
  RejectSessionParams,
  SubmitVerdictParams,
  UpdatePageQrScanParams
} from '../types';

export function useStartReviewMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('qrReview');

  return useMutation({
    mutationFn: (id: number) => qrReviewApi.startReview(id),
    onSuccess: (_data, id) => {
      toast.success(t('startReviewSuccess'));
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.detail(id) });
    }
  });
}

export function useEndSessionMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('qrReview');

  return useMutation({
    mutationFn: (id: number) => qrReviewApi.endSession(id),
    onSuccess: (_data, id) => {
      toast.success(t('endSessionSuccess'));
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.detail(id) });
    }
  });
}

export function useRejectSessionMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('qrReview');

  return useMutation({
    mutationFn: (params: RejectSessionParams) =>
      qrReviewApi.rejectSession(params),
    onSuccess: (_data, params) => {
      toast.success(t('rejectSuccess'));
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.lists() });
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.metrics() });
      queryClient.invalidateQueries({
        queryKey: qrReviewKeys.detail(params.batchId)
      });
    }
  });
}

export function useSubmitVerdictMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SubmitVerdictParams) =>
      qrReviewApi.submitVerdict(params),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: qrReviewKeys.detail(params.batchId)
      });
    }
  });
}

/** Persists a re-detected QR (bbox + decoded barcode) on a single page after
 *  the client-side decoder backfills a row, or after the operator clicks
 *  Rescan. Backend recomputes `qr_status` from `barcode` vs the page's
 *  expected `${container_id}-P${sequence}` value, so the overlay colour stays
 *  authoritative. Silent — no toast — because it usually fires from a backfill
 *  effect and would be noise. */
export function useUpdatePageQrScanMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdatePageQrScanParams) =>
      qrReviewApi.updatePageQrScan(params),
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: qrReviewKeys.detail(params.batchId)
      });
    }
  });
}

export function useEnhancePageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('qrReview');

  return useMutation({
    mutationFn: ({
      batchId,
      pageId,
      imageBlob,
      onProgress
    }: {
      batchId: number;
      pageId: number;
      imageBlob: Blob;
      /** Byte-level upload progress (0-100) for the enhanced image. */
      onProgress?: (progress: number) => void;
    }) => qrReviewApi.enhancePage(batchId, pageId, imageBlob, onProgress),
    onSuccess: (_data, { batchId }) => {
      toast.success(t('enhanceSuccess'));
      queryClient.invalidateQueries({ queryKey: qrReviewKeys.detail(batchId) });
    }
  });
}
